import { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Icon } from '@iconify/react'

// antd
import { Row, Typography, Upload, message, theme } from 'antd'
import { PlusOutlined } from '@ant-design/icons'

// assets
import { FileAddIcon } from '../../../assets/icons'

// slices
import {
    getS3UrlForAttachment,
    mainTaskUpdate,
    taskFileError,
    taskFilesRemove,
    taskFilesUpload,
    tasksState,
    taskUpdateSupport,
} from '../../taskRefactored/tasks.slice'
import {
    buildMainTaskUpdateBodyFromTask,
    isTaskMutationSuccess,
} from '../../taskRefactored/tasks.service'

// constants
import { fileTypes, pdfTypes, videoTypes } from '../../../constants/mediaTypes'

import dayjs from 'dayjs'

// styles
import styles from '../../taskRefactored/tasks.module.css'
import { uploadAttachmentToAWS } from '../../taskRefactored/tasks.service'
import MediaViewer from '../../taskRefactored/components/mediaViewer'
import {
    confirmOversizedUpload,
    MAX_ATTACHMENT_SIZE_MB,
} from './confirmOversizedUpload'

const ProjectAttachmentSection = ({
    taskId,
    onTaskUpdate,
    taskListKind = 'support',
    readOnly = false,
}) => {
    const isMainTask = taskListKind === 'main'
    const { Title } = Typography

    const { useToken } = theme
    const { token } = useToken()

    const dispatch = useDispatch()

    const { taskFiles, fileError } = useSelector(tasksState)
    const { selectedTask } = useSelector(tasksState)

    const [stream, setStream] = useState(false)
    const [fileURL, setFileURL] = useState([])
    const uploadTrackingRef = useRef({
        pending: new Set(),
        completed: [],
    })
    const oversizedBatchRef = useRef({
        names: [],
        uploadingCount: 0,
        timer: null,
    })

    useEffect(() => {
        if (selectedTask?.data?.attachment) {
            const attachments = selectedTask?.data?.attachment || []
            dispatch(taskFilesRemove(attachments))
            setFileURL(attachments)
        } else {
            dispatch(taskFilesRemove([]))
            setFileURL([])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTask?.data?.attachment, taskId])

    useEffect(() => {
        return () => {
            if (oversizedBatchRef.current.timer) {
                clearTimeout(oversizedBatchRef.current.timer)
            }
        }
    }, [])

    const uploadButton = (
        <div>
            <PlusOutlined />
        </div>
    )

    // UPLOADING BUTTON
    const uploadButtonLoading = (
        <div
            className={styles.uploadButtonBox}
            style={{
                border: '1px solid #edeae9',
            }}
        >
            <Icon
                icon="line-md:uploading-loop"
                width={26}
                height={26}
                color={token.colorPalette.baseColor.tertiary}
            />
        </div>
    )

    const persistAttachments = async (allAttachments) => {
        if (!taskId || !selectedTask?.data) return false

        if (isMainTask) {
            const task = selectedTask.data
            const body = buildMainTaskUpdateBodyFromTask(
                task,
                task?.projectId,
                { attachment: allAttachments }
            )
            const result = await dispatch(mainTaskUpdate({ id: taskId, body }))
            return isTaskMutationSuccess(result?.payload?.data)
        }

        const payload = {
            _id: taskId,
            updateTask: {
                title: selectedTask?.data?.title || '',
                projectId: selectedTask?.data?.projectId || null,
                type: selectedTask?.data?.type || 'Task',
                assigneeId: selectedTask?.data?.assigneeId?._id || null,
                priority: selectedTask?.data?.priority || 'Low',
                status: selectedTask?.data?.status || 'To Do',
                description: selectedTask?.data?.description || '',
                start: selectedTask?.data?.start || null,
                end: selectedTask?.data?.end || null,
                attachment: allAttachments,
            },
        }
        const result = await dispatch(taskUpdateSupport(payload))
        return Boolean(result?.payload?.data?.success)
    }

    const handleAllUploadsComplete = async () => {
        if (
            uploadTrackingRef.current.pending.size === 0 &&
            uploadTrackingRef.current.completed.length > 0
        ) {
            // Get current fileURL state using functional update
            setFileURL((currentFileURL) => {
                const allAttachments = [
                    ...currentFileURL,
                    ...uploadTrackingRef.current.completed,
                ]

                // Update Redux state with all new files
                uploadTrackingRef.current.completed.forEach((link) => {
                    dispatch(taskFilesUpload(link))
                })

                if (taskId && selectedTask?.data) {
                    persistAttachments(allAttachments)
                        .then((ok) => {
                            if (ok) {
                                onTaskUpdate?.()
                            } else {
                                message.error(
                                    'Failed to update task attachments'
                                )
                            }
                        })
                        .catch(() => {
                            message.error('Failed to update task attachments')
                        })
                }

                // Reset tracking
                uploadTrackingRef.current.completed = []

                return allAttachments
            })
        }
    }

    const props = {
        name: 'files',
        multiple: true,
        async onChange(info) {
            if (fileError?.length === 0) {
                const fileId = `${info?.file?.name}_${dayjs(new Date()).unix()}`
                uploadTrackingRef.current.pending.add(fileId)
                setStream(true)

                const nameChange = info?.file?.name
                    .split('.')[0]
                    .concat(`_${dayjs(new Date()).unix()}`)
                    .concat(
                        `.${
                            info?.file?.name.split('.')[
                                info?.file?.name.split('.')?.length - 1
                            ]
                        }`
                    )

                window.Buffer = window.Buffer || require('buffer').Buffer

                const formData = {
                    data: [
                        {
                            filename: nameChange,
                            contentType: info?.file?.type,
                        },
                    ],
                }

                dispatch(getS3UrlForAttachment(formData))
                    .then(async (data) => {
                        if (data?.payload?.data?.success) {
                            const response = data?.payload?.data?.data
                            const uploadUrl = response?.[0]?.uploadUrl
                            const publicLink = response?.[0]?.publicLink

                            if (uploadUrl) {
                                const formData = {
                                    url: uploadUrl,
                                    data: info?.file,
                                }
                                await uploadAttachmentToAWS(formData)
                                    .then((resp) => {
                                        console.log('Log ==>then resp', resp)
                                    })
                                    .finally(() => {
                                        setStream(false)
                                    })

                                // Mark this file as completed
                                uploadTrackingRef.current.pending.delete(fileId)
                                uploadTrackingRef.current.completed.push(
                                    publicLink
                                )

                                // Check if all uploads are complete
                                if (
                                    uploadTrackingRef.current.pending.size === 0
                                ) {
                                    await handleAllUploadsComplete()
                                }
                            } else {
                                uploadTrackingRef.current.pending.delete(fileId)
                                message.error(
                                    'Upload Failed!. Please Upload again'
                                )
                                setStream(false)
                            }
                        } else {
                            uploadTrackingRef.current.pending.delete(fileId)
                            message.error('Upload Failed!. Please Upload again')
                            setStream(false)
                        }
                    })
                    .catch((error) => {
                        uploadTrackingRef.current.pending.delete(fileId)
                        message.error('Upload Failed!. Please Upload again')
                        setStream(false)
                    })
            }
        },
        beforeUpload(file, fileList) {
            dispatch(taskFileError(''))

            const isWithinLimit =
                file.size / 1024 / 1024 <= MAX_ATTACHMENT_SIZE_MB
            if (!isWithinLimit) {
                const batch = oversizedBatchRef.current
                const selectedCount = Array.isArray(fileList)
                    ? fileList.length
                    : 1
                batch.uploadingCount = Math.max(
                    batch.uploadingCount || 0,
                    selectedCount
                )
                if (file?.name && !batch.names.includes(file.name)) {
                    batch.names.push(file.name)
                }
                if (batch.timer) clearTimeout(batch.timer)
                batch.timer = setTimeout(() => {
                    const rejectedNames = [...batch.names]
                    const uploadingCount = batch.uploadingCount
                    batch.names = []
                    batch.uploadingCount = 0
                    batch.timer = null
                    if (rejectedNames.length > 0) {
                        confirmOversizedUpload({
                            fileNames: rejectedNames,
                            uploadingCount,
                            maxSizeMb: MAX_ATTACHMENT_SIZE_MB,
                        })
                    }
                }, 0)
                return Upload.LIST_IGNORE
            }

            return false
        },
        onDrop() {},
    }

    const handleDelete = async (file) => {
        const removeData = [...fileURL]
        removeData.splice(
            removeData.findIndex((item) => item === file),
            1
        )
        setFileURL(removeData)
        dispatch(taskFilesRemove(removeData))

        if (taskId && selectedTask?.data) {
            const ok = await persistAttachments(removeData)
            if (ok) {
                onTaskUpdate?.()
            } else {
                message.error('Failed to update task attachments')
            }
        }
    }

    return (
        <>
            <div
                style={{
                    padding: '0px 25px',
                }}
            >
                <div
                    style={{
                        marginTop: '20px',
                    }}
                >
                    <Row
                        align={'middle'}
                        style={{
                            columnGap: '6px',
                            width: '100%',
                        }}
                    >
                        <FileAddIcon />
                        <Title className="titleSub">Add Files</Title>
                        <span style={{ color: '#363434', marginTop: '8px' }}>
                            (Please upload a{' '}
                            {[...fileTypes, ...videoTypes, ...pdfTypes].join(
                                ', '
                            )}{' '}
                            file.)
                        </span>
                    </Row>
                    <Row
                        style={{
                            width: '100%',
                            flexFlow: 'inherit',
                            marginTop: '20px',
                            columnGap: '10px',
                            rowGap: '10px',
                            flexWrap: 'wrap',
                        }}
                        className="fileTask"
                    >
                        {stream && uploadButtonLoading}
                        {taskFiles?.length > 0 && (
                            <MediaViewer
                                mediaFiles={taskFiles}
                                handleDelete={
                                    readOnly ? undefined : handleDelete
                                }
                            />
                        )}
                        {!readOnly && (
                            <Upload
                                listType="picture-card"
                                accept={[
                                    ...fileTypes,
                                    ...videoTypes,
                                    ...pdfTypes,
                                ]}
                                {...props}
                                fileList={[]}
                                rootClassName={styles.taskUploadAttachment}
                                className="attachmentUploadWrapper"
                            >
                                {uploadButton}
                            </Upload>
                        )}
                    </Row>
                    {fileError &&
                        (typeof fileError === 'string'
                            ? fileError.length > 0
                            : fileError?.error || fileError?.message) && (
                            <span style={{ color: 'red', margin: 2 }}>
                                {typeof fileError === 'string'
                                    ? fileError
                                    : fileError?.error ||
                                      fileError?.message ||
                                      JSON.stringify(fileError)}
                            </span>
                        )}
                </div>
            </div>
        </>
    )
}

export default ProjectAttachmentSection
